import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BOARD_HEIGHT, BOARD_WIDTH, UNIT_LIMIT, WAVES_PER_MAP } from "../constants/board";
import { normalizeDifficultyMode } from "../constants/difficulty";
import { MAPS } from "../constants/maps";
import { TOWER_DEFS } from "../constants/towers";
import { getWaveDefinition } from "../constants/waves";
import { getAdaptivePreviewContext, getScreenForState, getPathDataForState, selectCell, sellSelectedTower, startWave as engineStartWave, tickGame, toggleAutoRun as engineToggleAutoRun, upgradeSelectedTower } from "../engine/engine";
import { cloneStateForSave, createGameState, restoreRunState } from "../engine/gameState";
import { renderGame } from "../rendering/canvasRenderer";
import { clientToBoardPoint, pointToCell } from "../utils/path";
import { showRewardedAd } from "../../services/ads/adService";
import { applyReward, canClaimReward } from "../../services/ads/rewardRules";
import { createSoundService } from "../../services/audio/soundService";
import { syncDailyProgress } from "../../services/firebase/dailyService";
import { triggerHaptic } from "../../services/haptics";
import { submitLeaderboardScore } from "../../services/firebase/leaderboardService";
import { buildSavePayload, clearSavedGame, hasAnySave, loadBestAvailableSave, saveGamePayload } from "../../services/storage/saveRepository";
import { hasLocalSave } from "../../services/storage/localStorageService";

const snapshotFromState = (state, notice = null, pathData = null) => {
	const screen = getScreenForState(state);
	const selectedPlaced = state?.towers?.find((tower) => tower.id === state.selectedPlacedId) || null;
	const previewPath = pathData || (state ? getPathDataForState(state) : null);

	return {
		screen,
		runMode: state?.runMode || "campaign",
		difficultyMode: normalizeDifficultyMode(state?.difficultyMode),
		gold: state?.gold || 0,
		lives: state?.lives || 0,
		wave: state?.wave || 0,
		score: state?.score || 0,
		mapIdx: state?.mapIdx || 0,
		selectedTower: state?.selectedTower || null,
		selectedPlaced,
		paused: Boolean(state?.paused),
		autoRun: Boolean(state?.autoRun),
		speed: state?.speed || 1,
		waveActive: Boolean(state?.waveActive),
		placedCount: state?.towers?.length || 0,
		stats: state?.stats || {},
		nextWavePreview: state && !state.waveActive
			? getWaveDefinition({
				runMode: state.runMode,
				waveIndex: state.wave,
				mapIdx: state.mapIdx,
				mapDef: state.mapDef,
				runSeed: state.runSeed,
				adaptiveContext: getAdaptivePreviewContext(state, previewPath),
			})
			: [],
		notice,
		hasSave: hasLocalSave(),
		scoreMultiplier: state?.adRewards?.activeScoreMultiplier || null,
	};
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 2.35;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const clampViewport = (viewport) => {
	const scale = clamp(viewport.scale || MIN_ZOOM, MIN_ZOOM, MAX_ZOOM);
	const minX = BOARD_WIDTH - BOARD_WIDTH * scale;
	const minY = BOARD_HEIGHT - BOARD_HEIGHT * scale;

	return {
		scale,
		offsetX: scale === 1 ? 0 : clamp(viewport.offsetX || 0, minX, 0),
		offsetY: scale === 1 ? 0 : clamp(viewport.offsetY || 0, minY, 0),
	};
};

const pointerDistance = (a, b) => Math.hypot(a.screenX - b.screenX, a.screenY - b.screenY);

const midpoint = (a, b) => ({
	screenX: (a.screenX + b.screenX) / 2,
	screenY: (a.screenY + b.screenY) / 2,
});

const isMobileCanvasLayout = () => {
	if (typeof window === "undefined") return false;
	return window.matchMedia("(max-width: 820px), (pointer: coarse)").matches;
};

const capturePointer = (element, pointerId) => {
	try {
		element?.setPointerCapture?.(pointerId);
	} catch {
		// Synthetic pointer events and some WebViews may reject capture even though normal touch input works.
	}
};

const releasePointer = (element, pointerId) => {
	try {
		if (element?.hasPointerCapture?.(pointerId)) element.releasePointerCapture(pointerId);
	} catch {
		// Matching defensive capture handling above.
	}
};

export const useGameController = ({ user, profile, canPlay = true, loginRequired = false, settings, onSettingsLoaded }) => {
	const canvasRef = useRef(null);
	const stateRef = useRef(null);
	const pathDataRef = useRef(null);
	const animRef = useRef(null);
	const lastTickRef = useRef(0);
	const settingsRef = useRef(settings);
	const soundRef = useRef(null);
	const progressRef = useRef({});
	const viewportRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 });
	const activePointersRef = useRef(new Map());
	const gestureRef = useRef({ mode: null });
	const [ui, setUi] = useState(() => ({
		...snapshotFromState(null),
		screen: "menu",
		hasSave: hasLocalSave(),
	}));
	const [saving, setSaving] = useState(false);
	const [loadingSave, setLoadingSave] = useState(false);
	const [rewarding, setRewarding] = useState(false);
	const rewardLockRef = useRef(false);
	const lastLeaderboardSubmissionRef = useRef("");
	const clearedGameOverSaveRef = useRef(false);
	const dailyProgressRef = useRef({
		kills: 0,
		wavesCleared: 0,
		score: 0,
		towersBuilt: 0,
		survivalWaves: 0,
		campaignMaps: 0,
		goldSpent: 0,
		towersUpgraded: 0,
		towersSold: 0,
		bossesKilled: 0,
		campaignRecorded: false,
	});

	useEffect(() => {
		settingsRef.current = settings;
		soundRef.current?.syncSettings();
	}, [settings]);

	useEffect(() => {
		soundRef.current = createSoundService(settingsRef);
		return () => soundRef.current?.dispose();
	}, []);

	useEffect(() => {
		soundRef.current?.setMusicMode(ui.screen === "playing" ? "gameplay" : "home");
	}, [ui.screen]);

	const unlockSound = useCallback(() => {
		soundRef.current?.unlock();
	}, []);

	const showNotice = useCallback((message, tone = "info") => {
		setUi((previous) => ({
			...previous,
			notice: {
				id: Date.now(),
				message,
				tone,
				createdAt: Date.now(),
			},
		}));
	}, []);

	const refreshSaveAvailability = useCallback(async () => {
		try {
			const available = await hasAnySave(user);
			setUi((previous) => ({ ...previous, hasSave: available }));
			return available;
		} catch (error) {
			console.error("Save availability check failed.", error);
			return hasLocalSave();
		}
	}, [user]);

	const ensureCanPlay = useCallback(() => {
		if (!loginRequired || canPlay) return true;
		showNotice("Log in or create an account to play Stellar Siege.", "warn");
		return false;
	}, [canPlay, loginRequired, showNotice]);

	const syncUI = useCallback((notice = null) => {
		const state = stateRef.current;
		if (state?.notifications?.length) {
			const latest = state.notifications[state.notifications.length - 1];
			state.notifications = [];
			notice = latest;
		}

		setUi((previous) => {
			const retainedNotice = notice || (!notice && previous.notice && Date.now() - previous.notice.createdAt < 2600 ? previous.notice : null);
			const next = state ? snapshotFromState(state, retainedNotice, pathDataRef.current) : { ...previous, hasSave: hasLocalSave(), notice: retainedNotice };
			return next;
		});
	}, []);

	const resetDailyProgress = useCallback((state) => {
		dailyProgressRef.current = {
			kills: state?.stats?.kills || 0,
			wavesCleared: state?.stats?.wavesCleared || 0,
			score: state?.score || 0,
			towersBuilt: state?.stats?.towersBuilt || 0,
			survivalWaves: state?.stats?.survivalWavesCleared || 0,
			campaignMaps: state?.stats?.campaignMapsCleared || 0,
			goldSpent: state?.stats?.goldSpent || 0,
			towersUpgraded: state?.stats?.towersUpgraded || 0,
			towersSold: state?.stats?.towersSold || 0,
			bossesKilled: state?.stats?.bossesKilled || 0,
			campaignRecorded: false,
		};
	}, []);

	const flushDailyProgress = useCallback((options = {}) => {
		const state = stateRef.current;
		if (!state || !user?.uid) return;

		const current = dailyProgressRef.current;
		const campaignMapDelta = options.campaignMapCleared && !current.campaignRecorded ? 1 : 0;
		const delta = {
			kills: Math.max(0, (state.stats?.kills || 0) - current.kills),
			wavesCleared: Math.max(0, (state.stats?.wavesCleared || 0) - current.wavesCleared),
			scoreGained: Math.max(0, state.score - current.score),
			towersBuilt: Math.max(0, (state.stats?.towersBuilt || 0) - current.towersBuilt),
			survivalWaves: Math.max(0, (state.stats?.survivalWavesCleared || 0) - current.survivalWaves),
			campaignMaps: campaignMapDelta,
			goldSpent: Math.max(0, (state.stats?.goldSpent || 0) - current.goldSpent),
			towersUpgraded: Math.max(0, (state.stats?.towersUpgraded || 0) - current.towersUpgraded),
			towersSold: Math.max(0, (state.stats?.towersSold || 0) - current.towersSold),
			bossesKilled: Math.max(0, (state.stats?.bossesKilled || 0) - current.bossesKilled),
		};

		if (!Object.values(delta).some(Boolean)) return;

		dailyProgressRef.current = {
			kills: state.stats?.kills || 0,
			wavesCleared: state.stats?.wavesCleared || 0,
			score: state.score,
			towersBuilt: state.stats?.towersBuilt || 0,
			survivalWaves: state.stats?.survivalWavesCleared || 0,
			campaignMaps: current.campaignMaps + campaignMapDelta,
			goldSpent: state.stats?.goldSpent || 0,
			towersUpgraded: state.stats?.towersUpgraded || 0,
			towersSold: state.stats?.towersSold || 0,
			bossesKilled: state.stats?.bossesKilled || 0,
			campaignRecorded: current.campaignRecorded || campaignMapDelta > 0,
		};

		syncDailyProgress(user, delta).catch((error) => {
			console.error("Daily progress sync failed.", error);
		});
	}, [user]);

	const startMap = useCallback((mapIdx, carryScore = 0, runMode = "campaign", difficultyMode = settingsRef.current.difficultyMode) => {
		const mapDef = MAPS[mapIdx] || MAPS[0];
		const state = createGameState({ mapIdx, mapDef, carryScore, runMode, difficultyMode });
		lastLeaderboardSubmissionRef.current = "";
		clearedGameOverSaveRef.current = false;
		stateRef.current = state;
		resetDailyProgress(state);
		pathDataRef.current = getPathDataForState(state);
		viewportRef.current = { scale: 1, offsetX: 0, offsetY: 0 };
		activePointersRef.current.clear();
		gestureRef.current = { mode: null };
		lastTickRef.current = 0;
		syncUI({
			id: Date.now(),
			message: `${MAPS[mapIdx].name} ${runMode === "survival" ? "survival lane" : "defense grid"} ready (${normalizeDifficultyMode(difficultyMode)})`,
			tone: "accent",
			createdAt: Date.now(),
		});
	}, [resetDailyProgress, syncUI]);

	const startCampaign = useCallback(() => {
		if (!ensureCanPlay()) return;
		unlockSound();
		soundRef.current?.play("ui", 1);
		progressRef.current = {};
		startMap(0, 0, "campaign", settingsRef.current.difficultyMode);
	}, [ensureCanPlay, startMap, unlockSound]);

	const startSurvival = useCallback((mapIdx) => {
		if (!ensureCanPlay()) return;
		unlockSound();
		soundRef.current?.play("ui", 1);
		startMap(mapIdx, 0, "survival", settingsRef.current.difficultyMode);
	}, [ensureCanPlay, startMap, unlockSound]);

	const advanceMap = useCallback(() => {
		const state = stateRef.current;
		if (!state) return;
		const nextMap = state.mapIdx + 1;
		if (nextMap >= MAPS.length) {
			syncUI();
			return;
		}
		startMap(nextMap, state.score, "campaign", state.difficultyMode);
	}, [startMap, syncUI]);

	const backToMenu = useCallback((notice = null) => {
		flushDailyProgress();
		unlockSound();
		soundRef.current?.setMusicMode("home");
		lastLeaderboardSubmissionRef.current = "";
		clearedGameOverSaveRef.current = false;
		stateRef.current = null;
		pathDataRef.current = null;
		viewportRef.current = { scale: 1, offsetX: 0, offsetY: 0 };
		activePointersRef.current.clear();
		gestureRef.current = { mode: null };
		setUi((previous) => ({ ...snapshotFromState(null), screen: "menu", hasSave: previous.hasSave, notice }));
	}, [flushDailyProgress, unlockSound]);

	const save = useCallback(async () => {
		const state = stateRef.current;
		if (!state) return { ok: false };

		setSaving(true);
		try {
			const payload = buildSavePayload({
				user,
				profile,
				runState: cloneStateForSave(state),
				settings: settingsRef.current,
				previousProgress: progressRef.current,
			});
			progressRef.current = payload.progress;
			const target = await saveGamePayload({ user, payload });
			await refreshSaveAvailability();
			syncUI({
				id: Date.now(),
				message: target === "cloud" ? "Cloud save synchronized" : "Local save stored",
				tone: "success",
				createdAt: Date.now(),
			});
			return { ok: true, target };
		} catch (error) {
			console.error("Save failed.", error);
			syncUI({
				id: Date.now(),
				message: error.message || "Save failed",
				tone: "danger",
				createdAt: Date.now(),
			});
			return { ok: false, error };
		} finally {
			setSaving(false);
		}
	}, [profile, syncUI, user]);

	const load = useCallback(async () => {
		if (!ensureCanPlay()) return false;
		unlockSound();
		setLoadingSave(true);
		try {
			const result = await loadBestAvailableSave(user);
			if (!result?.payload?.runState) {
				await refreshSaveAvailability();
				syncUI({
					id: Date.now(),
					message: "No save found",
					tone: "warn",
					createdAt: Date.now(),
				});
				return false;
			}

			const restored = restoreRunState(result.payload.runState);
			stateRef.current = restored;
			clearedGameOverSaveRef.current = false;
			resetDailyProgress(restored);
			pathDataRef.current = getPathDataForState(restored);
			progressRef.current = result.payload.progress || {};
			if (result.payload.settings) onSettingsLoaded?.(result.payload.settings);
			lastTickRef.current = 0;
			await refreshSaveAvailability();
			syncUI({
				id: Date.now(),
				message: result.source === "cloud" ? "Cloud run restored" : "Local run restored",
				tone: "success",
				createdAt: Date.now(),
			});
			return true;
		} catch (error) {
			console.error("Load failed.", error);
			syncUI({
				id: Date.now(),
				message: error.message || "Load failed",
				tone: "danger",
				createdAt: Date.now(),
			});
			return false;
		} finally {
			setLoadingSave(false);
		}
	}, [ensureCanPlay, onSettingsLoaded, resetDailyProgress, syncUI, unlockSound, user]);

	const startWave = useCallback(() => {
		const state = stateRef.current;
		if (!state) return;
		unlockSound();
		if (engineStartWave(state, pathDataRef.current)) triggerHaptic(settingsRef.current.vibration, "Medium");
		syncUI();
	}, [syncUI, unlockSound]);

	const togglePause = useCallback(() => {
		const state = stateRef.current;
		if (!state) return;
		unlockSound();
		soundRef.current?.play("ui", 0.8);
		state.paused = !state.paused;
		syncUI();
	}, [syncUI, unlockSound]);

	const toggleSpeed = useCallback(() => {
		const state = stateRef.current;
		if (!state) return;
		unlockSound();
		soundRef.current?.play("ui", 0.8);
		state.speed = state.speed === 1 ? 2 : 1;
		syncUI();
	}, [syncUI, unlockSound]);

	const toggleAutoRun = useCallback(() => {
		const state = stateRef.current;
		if (!state) return;
		unlockSound();
		soundRef.current?.play("ui", 0.8);
		engineToggleAutoRun(state);
		syncUI({
			id: Date.now(),
			message: state.autoRun ? "Auto Run armed" : "Auto Run disabled",
			tone: state.autoRun ? "accent" : "info",
			createdAt: Date.now(),
		});
	}, [syncUI, unlockSound]);

	const clearSave = useCallback(async () => {
		try {
			await clearSavedGame(user);
			await refreshSaveAvailability();
			setUi((previous) => ({
				...previous,
				hasSave: false,
				notice: {
					id: Date.now(),
					message: "Saved game deleted",
					tone: "info",
					createdAt: Date.now(),
				},
			}));
			return true;
		} catch (error) {
			console.error("Save clear failed.", error);
			syncUI({
				id: Date.now(),
				message: error.message || "Could not delete saved game",
				tone: "danger",
				createdAt: Date.now(),
			});
			return false;
		}
	}, [syncUI, user]);

	useEffect(() => {
		refreshSaveAvailability();
	}, [refreshSaveAvailability]);

	const selectTower = useCallback((towerType) => {
		const state = stateRef.current;
		if (!state) return;
		unlockSound();
		soundRef.current?.play("ui", 0.7);
		state.selectedTower = state.selectedTower === towerType ? null : towerType;
		state.selectedPlacedId = null;
		state.placementPreview = null;
		syncUI();
	}, [syncUI, unlockSound]);

	const clearSelection = useCallback(() => {
		const state = stateRef.current;
		if (!state) return;
		state.selectedTower = null;
		state.selectedPlacedId = null;
		state.placementPreview = null;
		syncUI();
	}, [syncUI]);

	const updatePointer = useCallback((event) => {
		activePointersRef.current.set(event.pointerId, {
			clientX: event.clientX,
			clientY: event.clientY,
			pointerType: event.pointerType,
		});
	}, []);

	const screenPointFromPointer = useCallback((pointer) => {
		if (!canvasRef.current) return null;
		return clientToBoardPoint(canvasRef.current, pointer.clientX, pointer.clientY, { scale: 1, offsetX: 0, offsetY: 0 });
	}, []);

	const beginPinch = useCallback(() => {
		const pointers = [...activePointersRef.current.values()].slice(0, 2).map(screenPointFromPointer).filter(Boolean);
		if (pointers.length < 2) return false;

		const currentViewport = viewportRef.current;
		const center = midpoint(pointers[0], pointers[1]);
		const centerWorld = {
			x: (center.screenX - currentViewport.offsetX) / currentViewport.scale,
			y: (center.screenY - currentViewport.offsetY) / currentViewport.scale,
		};

		gestureRef.current = {
			mode: "pinch",
			startDistance: Math.max(1, pointerDistance(pointers[0], pointers[1])),
			startScale: currentViewport.scale,
			centerWorld,
		};

		const state = stateRef.current;
		if (state) {
			state.placementPreview = null;
			state.hoverCell = null;
		}

		return true;
	}, [screenPointFromPointer]);

	const updatePinch = useCallback(() => {
		const gesture = gestureRef.current;
		if (gesture.mode !== "pinch") return false;

		const pointers = [...activePointersRef.current.values()].slice(0, 2).map(screenPointFromPointer).filter(Boolean);
		if (pointers.length < 2) return false;

		const center = midpoint(pointers[0], pointers[1]);
		const distance = Math.max(1, pointerDistance(pointers[0], pointers[1]));
		const scale = clamp(gesture.startScale * (distance / gesture.startDistance), MIN_ZOOM, MAX_ZOOM);

		viewportRef.current = clampViewport({
			scale,
			offsetX: center.screenX - gesture.centerWorld.x * scale,
			offsetY: center.screenY - gesture.centerWorld.y * scale,
		});
		return true;
	}, [screenPointFromPointer]);

	const updatePlacementPreview = useCallback((event) => {
		const state = stateRef.current;
		if (!state || !canvasRef.current || !state.selectedTower) return;
		const point = pointToCell(canvasRef.current, event, viewportRef.current);
		state.hoverCell = point.col >= 0 && point.col < 20 && point.row >= 0 && point.row < 12 ? [point.col, point.row] : null;
		state.placementPreview = {
			active: true,
			type: state.selectedTower,
			x: point.x,
			y: point.y,
			col: point.col,
			row: point.row,
		};
	}, []);

	const onCanvasPointerMove = useCallback((event) => {
		const state = stateRef.current;
		if (!state || !canvasRef.current) return;
		updatePointer(event);

		if (activePointersRef.current.size >= 2) {
			event.preventDefault();
			updatePinch();
			return;
		}

		const gesture = gestureRef.current;
		if (gesture.mode === "place" && gesture.pointerId === event.pointerId) {
			event.preventDefault();
			updatePlacementPreview(event);
			return;
		}

		if (gesture.mode === "pan" && gesture.pointerId === event.pointerId) {
			event.preventDefault();
			const rect = canvasRef.current.getBoundingClientRect();
			const dx = ((event.clientX - gesture.startClientX) / rect.width) * BOARD_WIDTH;
			const dy = ((event.clientY - gesture.startClientY) / rect.height) * BOARD_HEIGHT;
			if (Math.hypot(dx, dy) > 8) gesture.moved = true;
			viewportRef.current = clampViewport({
				scale: gesture.startScale,
				offsetX: gesture.startOffsetX + dx,
				offsetY: gesture.startOffsetY + dy,
			});
			return;
		}

		if (gesture.mode === "tap" && gesture.pointerId === event.pointerId) {
			const dx = event.clientX - gesture.startClientX;
			const dy = event.clientY - gesture.startClientY;
			if (Math.hypot(dx, dy) > 10) gesture.moved = true;
		}

		const { col, row } = pointToCell(canvasRef.current, event, viewportRef.current);
		state.hoverCell = col >= 0 && col < 20 && row >= 0 && row < 12 ? [col, row] : null;
	}, [updatePinch, updatePlacementPreview, updatePointer]);

	const onCanvasPointerLeave = useCallback(() => {
		const state = stateRef.current;
		if (state && activePointersRef.current.size === 0) state.hoverCell = null;
	}, []);

	const onCanvasPointerDown = useCallback((event) => {
		const state = stateRef.current;
		if (!state || !canvasRef.current) return;
		event.preventDefault();
		unlockSound();
		capturePointer(canvasRef.current, event.pointerId);
		updatePointer(event);

		if (activePointersRef.current.size >= 2 && beginPinch()) return;

		if (state.selectedTower) {
			gestureRef.current = { mode: "place", pointerId: event.pointerId };
			updatePlacementPreview(event);
			triggerHaptic(settingsRef.current.vibration, "Light");
			return;
		}

		const viewport = viewportRef.current;
		if (viewport.scale > 1.01) {
			gestureRef.current = {
				mode: "pan",
				pointerId: event.pointerId,
				startClientX: event.clientX,
				startClientY: event.clientY,
				startScale: viewport.scale,
				startOffsetX: viewport.offsetX,
				startOffsetY: viewport.offsetY,
				moved: false,
			};
			return;
		}

		gestureRef.current = {
			mode: "tap",
			pointerId: event.pointerId,
			startClientX: event.clientX,
			startClientY: event.clientY,
			moved: false,
		};
	}, [beginPinch, unlockSound, updatePlacementPreview, updatePointer]);

	const onCanvasPointerUp = useCallback((event) => {
		const state = stateRef.current;
		const pathData = pathDataRef.current;
		if (!state || !pathData || !canvasRef.current) return;
		event.preventDefault();
		updatePointer(event);

		const gesture = gestureRef.current;
		const shouldPlace = gesture.mode === "place" && gesture.pointerId === event.pointerId;
		const shouldTapSelect = ["tap", "pan"].includes(gesture.mode) && gesture.pointerId === event.pointerId && !gesture.moved;

		if (shouldPlace || shouldTapSelect) {
			const { col, row } = pointToCell(canvasRef.current, event, viewportRef.current);
			selectCell(state, pathData, col, row);
			triggerHaptic(settingsRef.current.vibration, shouldPlace ? "Medium" : "Light");
			syncUI();
		}

		if (shouldPlace) {
			state.placementPreview = null;
			state.hoverCell = null;
		}

		activePointersRef.current.delete(event.pointerId);
		releasePointer(canvasRef.current, event.pointerId);
		gestureRef.current = { mode: null };

		if (activePointersRef.current.size >= 2) beginPinch();
	}, [beginPinch, syncUI, updatePointer]);

	const onCanvasPointerCancel = useCallback((event) => {
		const state = stateRef.current;
		activePointersRef.current.delete(event.pointerId);
		releasePointer(canvasRef.current, event.pointerId);
		if (state) {
			state.placementPreview = null;
			state.hoverCell = null;
		}
		gestureRef.current = { mode: null };
	}, []);

	const onCanvasWheel = useCallback((event) => {
		if (!canvasRef.current) return;
		event.preventDefault();
		const point = clientToBoardPoint(canvasRef.current, event.clientX, event.clientY, viewportRef.current);
		const scale = clamp(viewportRef.current.scale * (event.deltaY < 0 ? 1.08 : 0.92), MIN_ZOOM, MAX_ZOOM);
		viewportRef.current = clampViewport({
			scale,
			offsetX: point.screenX - point.x * scale,
			offsetY: point.screenY - point.y * scale,
		});
	}, []);

	const upgradeTower = useCallback(() => {
		unlockSound();
		if (upgradeSelectedTower(stateRef.current)) triggerHaptic(settingsRef.current.vibration, "Medium");
		syncUI();
	}, [syncUI, unlockSound]);

	const sellTower = useCallback(() => {
		unlockSound();
		sellSelectedTower(stateRef.current);
		triggerHaptic(settingsRef.current.vibration, "Light");
		syncUI();
	}, [syncUI, unlockSound]);

	const claimReward = useCallback(async (rewardId) => {
		if (rewardLockRef.current) return false;
		unlockSound();
		const state = stateRef.current;
		const check = canClaimReward(state, rewardId);
		if (!check.ok) {
			syncUI({
				id: Date.now(),
				message: check.reason,
				tone: "warn",
				createdAt: Date.now(),
			});
			return false;
		}

		rewardLockRef.current = true;
		setRewarding(true);

		try {
			const result = await showRewardedAd({ rewardId, userId: user?.uid || "guest" });
			if (!result.granted) throw new Error("Reward was not granted.");
			const applied = applyReward(state, rewardId);
			if (!applied.ok) throw new Error(applied.reason);
			triggerHaptic(settingsRef.current.vibration, "Heavy");
			syncUI();
			await save();
			return true;
		} catch (error) {
			console.error("Reward failed.", error);
			syncUI({
				id: Date.now(),
				message: error.message || "Reward unavailable",
				tone: "danger",
				createdAt: Date.now(),
			});
			return false;
		} finally {
			window.setTimeout(() => {
				rewardLockRef.current = false;
				setRewarding(false);
			}, 450);
		}
	}, [save, syncUI, unlockSound, user]);

	useEffect(() => {
		if (ui.screen !== "playing") return undefined;

		let uiCounter = 0;
		const loop = (timestamp) => {
			const state = stateRef.current;
			const pathData = pathDataRef.current;
			const dt = Math.min(timestamp - (lastTickRef.current || timestamp), 100);
			lastTickRef.current = timestamp;

			if (state && pathData) {
				tickGame(state, pathData, dt, settingsRef.current);
				if (state.soundCues?.length) {
					soundRef.current?.flush(state.soundCues);
					state.soundCues = [];
				}
				renderGame(canvasRef.current, state, pathData, {
					paused: state.paused,
					viewport: viewportRef.current,
					mobile: isMobileCanvasLayout(),
					performanceMode: settingsRef.current.performanceMode,
				});
			}

			uiCounter += dt;
			if (uiCounter > 160) {
				uiCounter = 0;
				syncUI();
			}
			animRef.current = requestAnimationFrame(loop);
		};

		animRef.current = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(animRef.current);
	}, [syncUI, ui.screen]);

	useEffect(() => {
		if (!stateRef.current || ui.screen !== "playing") return undefined;
		const interval = window.setInterval(() => {
			const state = stateRef.current;
			if (!state || getScreenForState(state) !== "playing") return;
			flushDailyProgress();
			save();
		}, 45000);
		return () => window.clearInterval(interval);
	}, [flushDailyProgress, save, ui.screen]);

	useEffect(() => {
		if (ui.screen !== "gameover" || clearedGameOverSaveRef.current) return undefined;
		clearedGameOverSaveRef.current = true;
		clearSavedGame(user)
			.then(() => {
				setUi((previous) => ({ ...previous, hasSave: false }));
			})
			.catch((error) => {
				console.error("Failed to clear defeated run save.", error);
			});
		return undefined;
	}, [ui.screen, user]);

	useEffect(() => {
		if (ui.screen !== "playing") return undefined;
		const interval = window.setInterval(() => {
			flushDailyProgress();
		}, 15000);
		return () => window.clearInterval(interval);
	}, [flushDailyProgress, ui.screen]);

	useEffect(() => {
		const state = stateRef.current;
		if (!state) return;
		if (!["mapComplete", "victory", "gameover"].includes(ui.screen)) return;
		if (state.runMode === "campaign" && ["mapComplete", "victory"].includes(ui.screen)) {
			state.stats.campaignMapsCleared = Math.max(state.stats?.campaignMapsCleared || 0, state.mapIdx + 1);
		}
		if (!user?.uid || !profile?.username) return;
		flushDailyProgress({
			campaignMapCleared: state.runMode === "campaign" && ["mapComplete", "victory"].includes(ui.screen),
		});

		const signature = `${ui.screen}:${state.mapIdx}:${state.wave}:${state.score}`;
		if (lastLeaderboardSubmissionRef.current === signature) return;
		lastLeaderboardSubmissionRef.current = signature;

		submitLeaderboardScore({
			user,
			profile,
			mode: state.runMode === "survival" ? "survival" : "campaign",
			score: state.score,
			mapIdx: state.mapIdx,
			wave: state.wave,
		}).catch((error) => {
			console.error("Leaderboard submission failed.", error);
		}).then((submitted) => {
			if (!submitted || typeof window === "undefined") return;
			window.dispatchEvent(new CustomEvent("stellar:leaderboard-updated"));
		});
	}, [flushDailyProgress, profile, ui.screen, user]);

	const currentMap = stateRef.current?.mapDef || MAPS[ui.mapIdx] || MAPS[0];
	const selectedDef = ui.selectedPlaced ? TOWER_DEFS[ui.selectedPlaced.type] : null;

	return useMemo(() => ({
		canvasRef,
		ui,
		currentMap,
		selectedDef,
		stateRef,
		limits: { unitLimit: UNIT_LIMIT, wavesPerMap: WAVES_PER_MAP, totalMaps: MAPS.length },
		actions: {
			startCampaign,
			startSurvival,
			startMap,
			advanceMap,
			backToMenu,
			load,
			save,
			startWave,
			togglePause,
			toggleSpeed,
			toggleAutoRun,
			clearSave,
			selectTower,
			clearSelection,
			upgradeTower,
			sellTower,
			claimReward,
			onCanvasPointerMove,
			onCanvasPointerLeave,
			onCanvasPointerDown,
			onCanvasPointerUp,
			onCanvasPointerCancel,
			onCanvasWheel,
		},
		loginRequired,
		canPlay,
		busy: {
			saving,
			loadingSave,
			rewarding,
		},
	}), [advanceMap, backToMenu, canPlay, claimReward, clearSave, clearSelection, currentMap, load, loginRequired, onCanvasPointerCancel, onCanvasPointerDown, onCanvasPointerLeave, onCanvasPointerMove, onCanvasPointerUp, onCanvasWheel, save, saving, loadingSave, rewarding, selectTower, selectedDef, sellTower, startCampaign, startMap, startSurvival, startWave, toggleAutoRun, togglePause, toggleSpeed, ui, upgradeTower]);
};
